import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DxTabsTypes } from 'devextreme-angular/ui/tabs';
import {
  BehaviorSubject,
  filter,
  finalize,
  Observable,
  switchMap,
  tap,
  zip,
  map,
  forkJoin,
} from 'rxjs';
import { TableColumn } from 'src/app/models/table-column.model';
import { TrainingCategoryFullModel, TrainingCategoryModel } from 'src/app/models/training-category/training-category.model';
import { TrainingGroupModel } from 'src/app/models/training-group.model';
import { TrainingTypeModel } from 'src/app/models/training/training-type.model';
import { LoadingService } from 'src/app/services/loading.service';
import { TrainingCategoryService } from 'src/app/services/training-categories/training-category.service';
import { TrainingGroupService } from 'src/app/services/training-group.service';
import { AdministrationTabsModel } from 'src/app/models/administration-tabs.model';
import { TextTemplateModel } from 'src/app/models/text-templates/text-template.model';
import { TextTemplateTypeModel } from 'src/app/models/text-templates/text-template-type.model';
import { TextTemplateService } from 'src/app/services/text-template.service';
import { DomSanitizer } from '@angular/platform-browser';
import { lockIcon } from 'src/app/app-icons';
import { PageStateService } from 'src/app/services/page-state.service';
import { PageStateFlags } from 'src/app/constants/page-state.constants';
import { MarketingChannelsComponent } from 'src/app/components/marketing-channels/marketing-channels.component';
import { TenantSubscriptionService } from 'src/app/services/tenants/tenant-subscription.service';
import { EventPublishingSettingsModel } from 'src/app/models/event-publishing-settings/event-publishing-settings.model';
import { EventPublishingSettingsService } from 'src/app/services/event-publishing-settings.service';
import { TenantEventPublishingSettingsFormComponent } from 'src/app/components/tenant-event-publishing-settings-form/tenant-event-publishing-settings-form.component';


@Component({
  selector: 'app-administration',
  templateUrl: './administration.component.html',
  styleUrl: './administration.component.scss',
  standalone: false
})
export class AdministrationComponent implements OnInit {
  @ViewChild('marketingChannels')
  marketingChannels!: MarketingChannelsComponent;

  @ViewChild('eventPublishingSettingsForm')
  eventPublishingSettingsForm!: TenantEventPublishingSettingsFormComponent;
  
  AdministrationTabs = AdministrationTabsModel;

  tabs: any[] = [
    {
      id: AdministrationTabsModel.TrainingGroups,
      text: this.translateService.instant(
        'ADMINISTRATION.OVERVIEW.TRAINING-GROUPS.TITLE'
      ),
      description: this.translateService.instant(
        'ADMINISTRATION.OVERVIEW.TRAINING-GROUPS.DESCRIPTION'
      ),
    },
    {
      id: AdministrationTabsModel.TrainingCategories,
      text: this.translateService.instant(
        'ADMINISTRATION.OVERVIEW.TRAINING-CATEGORIES.TITLE'
      ),
      description: this.translateService.instant(
        'ADMINISTRATION.OVERVIEW.TRAINING-CATEGORIES.DESCRIPTION'
      ),
    },
    {
      id: AdministrationTabsModel.MarketingChannels,
      text: this.translateService.instant(
        'ADMINISTRATION.OVERVIEW.MARKETING-CHANNELS.TITLE'
      ),
      description: this.translateService.instant(
        'ADMINISTRATION.OVERVIEW.MARKETING-CHANNELS.DESCRIPTION'
      ),
    },
    {
      id: AdministrationTabsModel.EventOverviewPage,
      text: this.translateService.instant(
        'ADMINISTRATION.OVERVIEW.EVENT-OVERVIEW-PAGE-TAB'
      ),
    },
    {
      id: AdministrationTabsModel.TextTemplates,
      text: this.translateService.instant(
        'ADMINISTRATION.OVERVIEW.TEXT-TEMPLATES.TITLE'
      ),
      description: this.translateService.instant(
        'ADMINISTRATION.OVERVIEW.TEXT-TEMPLATES.DESCRIPTION'
      ),
    },
  ];

  trainingGroupColumns: TableColumn[] = [
    {
      caption: this.translateService.instant('COMMON.FIELDS.NAME'),
      dataField: 'name',
      cssClass: 'width-250',
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.DESCRIPTION'),
      dataField: 'description',
      disableFiltering: true,
      cssClass: 'width-350',
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.TRAINING-TYPE'),
      dataField: 'trainingType',
      cellValue: (data: TrainingGroupModel): string => {
        switch (data.trainingType) {
          case TrainingTypeModel.General:
            return this.translateService.instant(
              'COMMON.FIELDS.TRAINING-TYPES.GENERAL'
            );
          case TrainingTypeModel.IDD:
            return this.translateService.instant(
              'COMMON.FIELDS.TRAINING-TYPES.IDD'
            );
          case TrainingTypeModel.Compliance:
            return this.translateService.instant(
              'COMMON.FIELDS.TRAINING-TYPES.COMPLIANCE'
            );
          default:
            return 'n.A.';
        }
      },
    },
    {
      caption: '',
      dataField: 'this',
      cellType: 'action',
      disableFiltering: true,
      cellTemplate: 'actionsTemplate',
      actions: [
        {
          icon: 'link-tab',
          isShowAction: () => true,
          onClick: (trainingGroup: TrainingGroupModel, e: any): void => {
            e.stopPropagation();
            e.preventDefault(); 
            e.stopImmediatePropagation();
            this.trainingGroupNavigateHandlerNewTab(trainingGroup.id);
          },
        },
      ],
    },
    // {
    //   caption: this.translateService.instant(
    //     'COMMON.FIELDS.QUALIFICATION-TARGET'
    //   ),
    //   dataField: 'qualificationTargetIddTimeMinutes',
    //   cellValue: (data: TrainingGroupModel): string => {
    //     return TimeService.getHoursAndMinutesDisplayStringFromIddTimeMinutes(
    //       data.qualificationTargetIddTimeMinutes || 0
    //     );
    //   },
    // },
  ];

  trainingCategoryColumns: TableColumn[] = [
    {
      caption: this.translateService.instant('COMMON.FIELDS.NAME'),
      dataField: 'name',
      cssClass: 'width-250',
      cellTemplate: 'trainingCategoryNameTemplate'
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.DESCRIPTION'),
      dataField: 'description',
      disableFiltering: true,
      cssClass: 'width-350',
    },
    {
      caption: '',
      dataField: 'this',
      cellType: 'action',
      disableFiltering: true,
      cellTemplate: 'actionsTemplate',
      actions: [
        {
          icon: 'link-tab',
          isShowAction: () => true,
          onClick: (trainingCategory: TrainingCategoryModel, e: any): void => {
            e.stopPropagation();
            e.preventDefault(); 
            e.stopImmediatePropagation();
            this.trainingCategoryNavigateHandlerNewTab(trainingCategory.id);
          },
        },
      ],
    },
  ];

  textTemplateColumns: TableColumn[] = [
    {
      caption: this.translateService.instant('COMMON.FIELDS.NAME'),
      dataField: 'name',
      cssClass: 'width-300',
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.TYPE'),
      dataField: 'type',
      cellValue: (data: TextTemplateModel): string => {
        switch (data.type) {
          case TextTemplateTypeModel.ExtendedDescription:
            return this.translateService.instant(
              'COMMON.FIELDS.TEXT-TEMPLATE-TYPES.EXTENDED-DESCRIPTION'
            );

          case TextTemplateTypeModel.AddParticipants:
            return this.translateService.instant(
              'COMMON.FIELDS.TEXT-TEMPLATE-TYPES.ADD-PARTICIPANTS'
            );

          case TextTemplateTypeModel.InfoToParticipants:
            return this.translateService.instant(
              'COMMON.FIELDS.TEXT-TEMPLATE-TYPES.INFO-TO-PARTICIPANTS'
            );

          case TextTemplateTypeModel.CancelMeeting:
            return this.translateService.instant(
              'COMMON.FIELDS.TEXT-TEMPLATE-TYPES.CANCEL-MEETING'
            );

          case TextTemplateTypeModel.ChangeMeeting:
            return this.translateService.instant(
              'COMMON.FIELDS.TEXT-TEMPLATE-TYPES.CHANGE-MEETING'
            );

          default:
            return 'n.A.';
        }
      },
    },
    {
      caption: '',
      dataField: 'this',
      cellType: 'action',
      disableFiltering: true,
      cellTemplate: 'actionsTemplate',
      actions: [
        {
          icon: 'link-tab',
          isShowAction: () => true,
          onClick: (template: TextTemplateModel, e: any): void => {
            e.stopPropagation();
            e.preventDefault(); 
            e.stopImmediatePropagation();
            this.textTemplateNavigateHandlerNewTab(template.id);
          },
        },
      ],
    },
  ];


  selectedTab$ = new BehaviorSubject<number>(AdministrationTabsModel.TrainingGroups);
  trainingCategories$!: Observable<TrainingCategoryFullModel[]>;
  trainingGroups$!: Observable<TrainingGroupModel[]>;
  textTemplates$!: Observable<TextTemplateModel[]>;
  
  selectedRows: string[] = [];

  isPublishedToSubscribers: boolean = false;
  eventPublishingSettings: EventPublishingSettingsModel | null = null;

  get selectedTab(): any {
    const selectedTab = this.selectedTab$.value;
    return this.tabs[selectedTab || AdministrationTabsModel.TrainingGroups];
  }

  get isTrainingGroupsTab() {
    return this.selectedTab$.getValue() === AdministrationTabsModel.TrainingGroups;
  }

  get isTrainingCategoriesTab() {
    return this.selectedTab$.getValue() === AdministrationTabsModel.TrainingCategories;
  }

  get isTextTemplatesTab() {
    return this.selectedTab$.getValue() === AdministrationTabsModel.TextTemplates;
  }

  get isEventOverviewPageTab() {
    return this.selectedTab$.getValue() === AdministrationTabsModel.EventOverviewPage;
  }

  get isMarketingChannelsTab() {
    return this.selectedTab$.getValue() === AdministrationTabsModel.MarketingChannels;
  }


  get showDeleteButton(): boolean {
    return this.isTrainingCategoriesTab || this.isTextTemplatesTab;
  }

  readonly trainingGroupsStateKey: string = 'training-groups-overview';
  readonly trainingCategoriesStateKey: string = 'training-categories-overview';
  readonly textTemplatesStateKey: string = 'text-templates-overview';
  readonly marketingChannelsStateKey: string = 'marketing-channels-overview';

  constructor(
    private route: ActivatedRoute,
    private spinnerService: LoadingService,
    private trainingCategoryService: TrainingCategoryService,
    private trainingGroupService: TrainingGroupService,
    private textTemplateService: TextTemplateService,
    private router: Router,
    private translateService: TranslateService,
    private sanitized: DomSanitizer,
    private pageStateService: PageStateService,
    private tenantSubscriptionService: TenantSubscriptionService,
    private loadingService: LoadingService,
    private eventPublishingSettingsService: EventPublishingSettingsService,
  ) {
    const tab = this.route.snapshot.queryParams['tab'];
    
    if (tab === 'training-categories') {
      this.selectedTab$.next(AdministrationTabsModel.TrainingCategories);
    }
    else if (tab === 'text-templates') {
      this.selectedTab$.next(AdministrationTabsModel.TextTemplates);
    }
    else if (tab === 'marketing-channels') {
      this.selectedTab$.next(AdministrationTabsModel.MarketingChannels);
    }
    else if (tab === 'event-overview-page') {
      this.selectedTab$.next(AdministrationTabsModel.EventOverviewPage);
    }
  }

  ngOnInit(): void {
    if (!this.pageStateService.consumeFlag(
      PageStateFlags.RestoreTrainingGroupsOverview
    )) {
      this.pageStateService.clear(this.trainingGroupsStateKey);
    }

    if (!this.pageStateService.consumeFlag(
      PageStateFlags.RestoreTrainingCategoriesOverview
    )) {
      this.pageStateService.clear(this.trainingCategoriesStateKey);
    }

    if (!this.pageStateService.consumeFlag(
      PageStateFlags.RestoreTextTemplatesOverview
    )) {
      this.pageStateService.clear(this.textTemplatesStateKey);
    }

    if (!this.pageStateService.consumeFlag(
      PageStateFlags.RestoreMarketingChannelsOverview
    )) {
      this.pageStateService.clear(this.marketingChannelsStateKey);
    }

    this.loadingService.load(
      forkJoin({
        isPublishedToSubscribers: this.tenantSubscriptionService.isPublishedToSubscribers(),
        eventPublishingSettings: this.eventPublishingSettingsService.getEventPublishingSettings()
      })
    ).subscribe(({
      isPublishedToSubscribers, eventPublishingSettings
    }) => {
      this.isPublishedToSubscribers = isPublishedToSubscribers;
      this.eventPublishingSettings = eventPublishingSettings;
    })

    this.trainingCategories$ = this.selectedTab$.pipe(
      tap(() => {
        this.selectedRows = [];
      }),
      filter((selectedTab) => selectedTab === AdministrationTabsModel.TrainingCategories),
      switchMap(() =>
        this.spinnerService.load(
          this.trainingCategoryService.getAllTrainingCategories()
        )
      ),
      map((response: any) => {
        return response.map((trainingCategory: TrainingCategoryModel) => ({
          ...trainingCategory,
        }));
      })
    );

    this.trainingGroups$ = this.selectedTab$.pipe(
      tap(() => {
        this.selectedRows = [];
      }),
      filter((selectedTab) => selectedTab === AdministrationTabsModel.TrainingGroups),
      switchMap(() =>
        this.spinnerService.load(
          this.trainingGroupService.getAllTrainingGroups()
        )
      )
    );

    this.textTemplates$ = this.selectedTab$.pipe(
      tap(() => {
        this.selectedRows = [];
      }),
      filter((selectedTab) => selectedTab === AdministrationTabsModel.TextTemplates),
      switchMap(() =>
        this.spinnerService.load(
          this.textTemplateService.getAllTextTemplates()
        )
      )
    );
  }

  getLockIcon() {
    return this.sanitized.bypassSecurityTrustHtml(lockIcon);
  }

  onTabChange(e: DxTabsTypes.ItemClickEvent) {
    this.selectedTab$.next(e.itemData.id);
  }

  onRowSelected(ids: string[]) {
    this.selectedRows = ids;
  }

  deleteSelected() {
    if (!this.selectedRows?.length) return;

    this.spinnerService
      .load(
        zip(
          this.selectedRows.map((id) => {
            if (this.isTrainingGroupsTab) {
              return this.trainingGroupService.deleteTrainingGroup(id);
            }

            if (this.isTrainingCategoriesTab) {
              return this.trainingCategoryService.deleteTrainingCategory(id);
            }

            if (this.isTextTemplatesTab) {
              return this.textTemplateService.deleteTextTemplate(id);
            }

            throw new Error('Unknown tab selected for delete operation');
          })
        ).pipe(
          finalize(() => {
            this.selectedTab$.next(this.selectedTab$.getValue());
          })
        )
      )
      .subscribe();
  }

  trainingCategoryNavigateHandler(id?: string) {
    this.router.navigate([
      'administration',
      'training-categories',
      id || 'new',
    ]);
  }

  trainingCategoryNavigateHandlerNewTab(id?: string) {
    const url = this.router.serializeUrl(this.router.createUrlTree(['administration', 'training-categories', id]));
    window.open(url, '_blank');
  }

  trainingGroupNavigateHandler(id?: string) {
    this.router.navigate(['administration', 'training-group', id || 'new']);
  }

  trainingGroupNavigateHandlerNewTab (id?: string) {
    const url = this.router.serializeUrl(this.router.createUrlTree(['administration', 'training-group', id || 'new']));
    window.open(url, '_blank');
  }

  textTemplateNavigateHandler(id?: string) {
    this.router.navigate(['administration', 'text-template', id || 'new']);
  }

  textTemplateNavigateHandlerNewTab(id?: string) {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['administration', 'text-template', id || 'new'])
    );
    window.open(url, '_blank');
  }

  onAddChannelClick() {
    this.marketingChannels.onAddChannelClick();
  }
}
